import React, { useEffect, useRef, useState } from 'react';
import { playTypewriterSound, stopTypewriterSound } from '../../../../utils/sounds';

export interface FastTypingHostProps {
  text: string;
  timeLimit: number;
  /** When true, stop typewriter animation and sound (e.g. everyone submitted). */
  roundEnded?: boolean;
  /** Delay in ms between revealing each character */
  charDelayMs?: number;
  /** When false, reveal silently (e.g. player phones while host is playing). Default true. */
  soundEnabled?: boolean;
  /**
   * Host-as-player on a phone: keep the prompt compact so the textarea stays visible
   * above the soft keyboard. Casting host / desktop keep the full typewriter size.
   */
  compact?: boolean;
}

/**
 * Host view: typewriter animation revealing the text character by character,
 * with optional typing sound. After reveal completes, full text stays visible.
 */
export const FastTypingHost: React.FC<FastTypingHostProps> = ({
  text,
  timeLimit,
  roundEnded = false,
  charDelayMs = 80,
  soundEnabled = true,
  compact = false,
}) => {
  const [visibleLength, setVisibleLength] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Stop typewriter and sound when round ends (e.g. everyone submitted)
  useEffect(() => {
    if (!roundEnded) return;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    stopTypewriterSound();
  }, [roundEnded]);

  useEffect(() => {
    if (!text || timeLimit <= 0 || roundEnded) return;
    setVisibleLength(0);

    // Play typing sound once when typewriter starts; stop it when round ends or text finishes
    if (soundEnabled) playTypewriterSound();

    intervalRef.current = setInterval(() => {
      setVisibleLength((prev) => {
        const next = prev + 1;
        if (next >= text.length && intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          stopTypewriterSound();
        }
        return Math.min(next, text.length);
      });
    }, charDelayMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      stopTypewriterSound();
    };
  }, [text, timeLimit, roundEnded, charDelayMs, soundEnabled]);

  const visibleText = text.slice(0, visibleLength);
  const cursor = visibleLength < text.length ? '|' : '';

  return (
    <div
      className={compact ? 'fast-typing-host fast-typing-host--compact' : 'fast-typing-host'}
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: compact ? '0 1 auto' : '1 1 auto',
        minHeight: 0,
        maxHeight: compact ? 'min(28vh, 180px)' : undefined,
        padding: compact ? '0.25rem 0.75rem' : '0.5rem 1rem',
      }}
    >
      <div
        className="fast-typing-host-text"
        style={{
          width: '100%',
          maxWidth: 720,
          padding: compact ? '0.65rem 0.85rem' : '1.25rem 1.5rem',
          fontFamily: 'ui-monospace, monospace',
          fontSize: compact ? 'clamp(0.78rem, 2.8vw, 0.95rem)' : 'clamp(1.25rem, 2.8vw, 1.6rem)',
          lineHeight: compact ? 1.35 : 1.6,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowY: compact ? 'auto' : undefined,
          maxHeight: compact ? '100%' : undefined,
          background: 'linear-gradient(135deg, rgba(var(--color-primary-rgb), 0.55) 0%, rgba(var(--color-secondary-rgb), 0.62) 100%)',
          borderRadius: 12,
          border: '2px solid rgba(var(--color-primary-rgb), 0.45)',
          boxShadow: 'var(--shadow-sm)',
          textAlign: 'left',
          boxSizing: 'border-box',
        }}
      >
        {visibleText}
        <span
          className="fast-typing-cursor"
          style={{
            opacity: visibleLength < text.length ? 1 : 0,
            animation: visibleLength < text.length ? 'fast-typing-blink 0.8s ease-in-out infinite' : 'none',
          }}
        >
          {cursor}
        </span>
      </div>
      <style>{`
        @keyframes fast-typing-blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0.3; }
        }
        @media (min-width: 1024px) {
          .fast-typing-host:not(.fast-typing-host--compact) .fast-typing-host-text { font-size: 1.95rem; }
        }
        @media (min-width: 1440px) {
          .fast-typing-host:not(.fast-typing-host--compact) .fast-typing-host-text { font-size: 2.85rem; }
        }
      `}</style>
    </div>
  );
};
